/**
 * TrendHero — Reusable cinematic full-screen hero
 *
 * Used on both TrendDetailPage (collection overview) and
 * TrendCardDetailPage (individual card).
 *
 * DESIGN (mirrors imagio PageHero cinematic mode):
 *   • Full-screen background image with brightness overlay
 *   • Vertically centred left-anchored content: chip → title → description → tags row
 *   • 4 floating frosted-glass slabs (hidden on mobile)
 *   • Diagonal light-streak CSS animation (rotate -16deg)
 *   • Single 1px scroll-hint line at bottom centre
 *   • Staggered entrance animations via CSS classes
 */

import { CovIcon, IconName } from "@cx-component-library/react-components";
import { useState } from "react";
import type { TrendColor } from "../trendData";
import "./TrendHero.css";

interface TrendHeroProps {
  heroImage: string;
  title: string;
  chip?: string;
  description?: string;
  /** Color swatches shown below the title, in place of text tag pills */
  swatches?: TrendColor[];
  /** Attribution byline shown below the description (e.g. "— Name, Title") */
  attribution?: string;
  tags?: string[];
  badge?: string;
  /** Label for the floating CMF tag — defaults to "CMF Collection" */
  tagLabel?: string;
  /** Label for the explore CTA button — defaults to "Explore Collection" */
  exploreLabel?: string;
  /** Show the "Explore Collection" scroll-down CTA — only on TrendDetailPage */
  showExplore?: boolean;
  /** Custom handler for the explore CTA — overrides default scroll-by-viewport */
  onExplore?: () => void;
  /** CTA style: "outline" (default, cinematic ghost button) or "primary"
   *  (solid blue text + down-arrow icon, used by the chapter sub-page). */
  exploreVariant?: "outline" | "primary";
  /** Truncate long descriptions without expand option */
  truncate?: boolean;
  /** Truncate with "Read more" link to expand */
  collapsible?: boolean;
}

const DESC_CHAR_LIMIT = 200;

export const TrendHero = ({
  heroImage,
  title,
  chip,
  description,
  swatches,
  attribution,
  tags,
  badge,
  tagLabel = "CMF Collection",
  exploreLabel = "Explore Collection",
  showExplore = false,
  onExplore,
  exploreVariant = "outline",
  truncate = false,
  collapsible = false,
}: TrendHeroProps) => {
  const [expanded, setExpanded] = useState(false);
  const shouldTruncate =
    (truncate || collapsible) &&
    !!description &&
    description.length > DESC_CHAR_LIMIT;

  const truncatedText = shouldTruncate
    ? description!.slice(0, DESC_CHAR_LIMIT).replace(/\s+\S*$/, "")
    : "";

  return (
    <section
      className={`trend-hero${expanded ? " trend-hero--expanded" : ""}`}
      aria-label={title}
    >
      {/* Background image */}
      <img
        src={heroImage}
        alt={title}
        className="trend-hero__bg"
        draggable={false}
        onError={(e) => {
          (e.currentTarget as HTMLImageElement).src =
            "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1' height='1'%3E%3Crect width='1' height='1' fill='%23222'/%3E%3C/svg%3E";
        }}
      />

      {/* Left-side legibility gradient */}
      <div className="trend-hero__gradient" aria-hidden="true" />

      {/* Bottom depth vignette */}
      <div className="trend-hero__vignette-bottom" aria-hidden="true" />

      {/* Top vignette */}
      <div className="trend-hero__vignette-top" aria-hidden="true" />

      {/* Chromatic top rule */}
      <div className="trend-hero__top-rule" aria-hidden="true" />

      {/* Diagonal light-streak */}
      <div className="trend-hero__streak" aria-hidden="true" />

      {/* Floating frosted slabs — positioned absolutely on hero (desktop only) */}
      <div
        className="trend-hero__slab trend-hero__slab--1"
        aria-hidden="true"
      />
      <div
        className="trend-hero__slab trend-hero__slab--2"
        aria-hidden="true"
      />
      <div
        className="trend-hero__slab trend-hero__slab--3"
        aria-hidden="true"
      />
      <div
        className="trend-hero__slab trend-hero__slab--4"
        aria-hidden="true"
      />

      {/* Content — vertically centred */}
      <div className="trend-hero__content">
        {chip && (
          <div className="trend-hero__chip trend-hero__chip--enter">{chip}</div>
        )}
        <h1 className="trend-hero__title trend-hero__title--enter">{title}</h1>
        {swatches && swatches.length > 0 && (
          <div className="trend-hero__swatches trend-hero__swatches--enter">
            {swatches.map((swatch) => (
              <span
                key={swatch.name}
                className="trend-hero__swatch"
                style={{ backgroundColor: swatch.hex }}
                role="img"
                aria-label={`Color: ${swatch.name}`}
              />
            ))}
          </div>
        )}
        {description && (
          <div className="trend-hero__description-wrap trend-hero__description--enter">
            <p className="trend-hero__description-text">
              {shouldTruncate ? truncatedText : description}
              {shouldTruncate && !expanded && "..."}
              {shouldTruncate && !expanded && collapsible && (
                <button
                  type="button"
                  className="trend-hero__read-more"
                  onClick={() => setExpanded(true)}
                >
                  Read more
                </button>
              )}
            </p>
            {shouldTruncate && collapsible && (
              <div
                className={`trend-hero__description${expanded ? " trend-hero__description--expanded" : ""}`}
              >
                <span className="trend-hero__description-inner">
                  {description.slice(truncatedText.length)}
                </span>
              </div>
            )}
            {shouldTruncate && expanded && collapsible && (
              <button
                type="button"
                className="trend-hero__read-less"
                onClick={() => setExpanded(false)}
              >
                Read less
                <CovIcon iconName={IconName.CHEVRON_UP} />
              </button>
            )}
          </div>
        )}
        {attribution && (
          <p className="trend-hero__attribution trend-hero__attribution--enter">
            {attribution}
          </p>
        )}
        {!swatches?.length && tags && tags.length > 0 && (
          <div className="trend-hero__tags trend-hero__tags--enter">
            {tags.map((tag) => (
              <span key={tag} className="trend-hero__tag">
                {tag}
              </span>
            ))}
          </div>
        )}
        {badge && (
          <div className="trend-hero__badge trend-hero__badge--enter">
            <span className="trend-hero__badge-dot" aria-hidden="true" />
            {badge}
          </div>
        )}
        {showExplore && (
          <button
            type="button"
            className={`trend-hero__cta trend-hero__cta--${exploreVariant} trend-hero__cta--enter`}
            onClick={() =>
              onExplore
                ? onExplore()
                : window.scrollBy({
                    top: window.innerHeight,
                    behavior: "smooth",
                  })
            }
          >
            {exploreLabel}
            {(exploreVariant === "primary" || onExplore) && (
              <CovIcon iconName={IconName.CHEVRON_DOWN} />
            )}
          </button>
        )}
      </div>

      {/* Floating CMF tag — bottom right */}
      <div className="trend-hero__library-tag" aria-hidden="true">
        <span className="trend-hero__library-tag-dot" aria-hidden="true" />
        {tagLabel}
      </div>

      {/* Scroll hint — single 1×36px animated line */}
      <div className="trend-hero__scroll-hint" aria-hidden="true">
        <div className="trend-hero__scroll-line" />
      </div>
    </section>
  );
};
